import { useReactFlow } from '@xyflow/react';
import { useCallback, useRef } from 'react';

/**
 * Hook that provides access to node dimensions that may change during runtime.
 * Uses React Flow's internal node data to get the current width and height of nodes
 * by looking at their handle bounds. Falls back to 1x1 dimensions if not found.
 * This is useful for elements like group labels whose size depends on their content.
 */

export const useDynamicNodeDimensions = ({
  minWidth = 1,
  minHeight = 1,
}: {
  minWidth?: number;
  minHeight?: number;
} = {}) => {
  const { getInternalNode } = useReactFlow();
  const nodeDimensions = useRef<{
    [id: string]: {
      width: number;
      height: number;
    };
  }>({});

  const getDynamicNodeDimensions = useCallback(
    (id: string) => {
      const internalNode = getInternalNode(id);
      if (internalNode) {
        const width = internalNode.internals.handleBounds?.source?.find(
          (handle) => handle.id === 'sr',
        )?.x;

        const height = internalNode.internals.handleBounds?.source?.find(
          (handle) => handle.id === 'sb',
        )?.y;
        if (width && height) {
          nodeDimensions.current[id] = {
            width: Math.max(width, minWidth),
            height: Math.max(height, minHeight),
          };
        }
      }
      return nodeDimensions.current[id] || { width: 1, height: 1 };
    },
    [getInternalNode, minHeight, minWidth],
  );

  return { getDynamicNodeDimensions };
};
